import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Document } from 'jsonapi-typescript';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { deserializeUserVehicleAssignments } from '../deserializers/user-vehicle-assignment.deserializer';
import { UserVehicleAssignmentPayload } from '../interfaces/user-vehicle-assignment-payload.interface';
import { UserVehicleAssignment } from '../interfaces/user-vehicle-assignment.interface';
import { serializeUserVehicleAssignment } from '../serializers/user-vehicle-assignment.serializer';
import { ENVIRONMENT } from '../tokens/environment.token';
import { HttpsParamsBuilderNew } from '../utils/params-builder-new.util';

const PAGE_SIZE = 300;

@Injectable({ providedIn: 'root' })
export class UserVehicleAssignmentsApiService {
	private httpClient = inject(HttpClient);
	private environment = inject(ENVIRONMENT);

	fetchByUserIds(userIds: string[]): Observable<UserVehicleAssignment[]> {
		return this.fetch({ 'user.id.in': userIds });
	}

	fetchByVehicleIds(vehicleIds: string[]): Observable<UserVehicleAssignment[]> {
		return this.fetch({ 'vehicle.id.in': vehicleIds });
	}

	create(assignment: UserVehicleAssignmentPayload): Observable<void> {
		return this.httpClient
			.post<Document>(
				this.environment.serverNew + '/user/v1/user-vehicle-assignments',
				serializeUserVehicleAssignment(assignment)
			)
			.pipe(map(() => undefined));
	}

	delete(assignmentId: string): Observable<void> {
		return this.httpClient.delete<void>(
			this.environment.serverNew + `/user/v1/user-vehicle-assignments/${assignmentId}`
		);
	}

	sync(deleteIds: string[], creates: UserVehicleAssignmentPayload[]): Observable<boolean[]> {
		return this.settle(deleteIds.map((id) => this.delete(id))).pipe(
			switchMap((deleted) =>
				this.settle(creates.map((payload) => this.create(payload))).pipe(
					map((created) => [...deleted, ...created])
				)
			)
		);
	}

	private settle(requests: Observable<void>[]): Observable<boolean[]> {
		if (requests.length === 0) {
			return of([]);
		}
		return forkJoin(
			requests.map((request) =>
				request.pipe(
					map(() => true),
					catchError(() => of(false))
				)
			)
		);
	}

	private fetch(filter: Record<string, string[]>): Observable<UserVehicleAssignment[]> {
		const params = HttpsParamsBuilderNew.buildParameters({
			pageSize: PAGE_SIZE,
			include: 'user',
			filter,
		});
		return this.httpClient
			.get<Document>(this.environment.serverNew + '/user/v1/user-vehicle-assignments', { params })
			.pipe(
				switchMap((document) => deserializeUserVehicleAssignments(document)),
				map((response) => response.assignments)
			);
	}
}
